import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LayoutDashboard, LineChart, ListChecks, Bell, Download, Settings2, Activity, TrendingUp, TrendingDown, Minus, Send, Mail, Loader2, BarChart3, Calculator, BookOpen, Shield, User, Users, Zap, Layers, ShieldAlert, PieChart, Cpu, Book, Home, LogOut, Crown, Server, Trophy, DollarSign, Landmark, Globe2, UploadCloud, HelpCircle } from "lucide-react";
import { API } from "@/lib/api";
export { API };
import { AppProvider, useApp } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangSwitcher, CurrencySwitcher } from "@/components/LangCurrencySwitcher";
import DiscordCTA from "@/components/DiscordCTA";
import { TrialEndedModal } from "@/components/TrialEndedModal";
import { FeedbackButton } from "@/components/FeedbackButton";
import UserGuide from "@/pages/UserGuide";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import AuthPage from "@/pages/AuthPage";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Dashboard from "@/pages/Dashboard";
import Watchlist from "@/pages/Watchlist";
import Signals from "@/pages/Signals";
import Downloads from "@/pages/Downloads";
import SettingsPage from "@/pages/SettingsPage";
import Backtest from "@/pages/Backtest";
import PositionSizer from "@/pages/PositionSizer";
import Lexique from "@/pages/Lexique";
import BetaStatus from "@/pages/BetaStatus";
import LexiquePublic from "@/pages/LexiquePublic";
import PropFirm from "@/pages/PropFirm";
import PropFirmAccounts from "@/pages/PropFirmAccounts";
import About from "@/pages/About";
import PropFirmCompare from "@/pages/PropFirmCompare";
import PropFirmQuiz from "@/pages/PropFirmQuiz";
import PropFirmArticle from "@/pages/PropFirmArticle";
import Leaderboard from "@/pages/Leaderboard";
import AdminAffiliates from "@/pages/AdminAffiliates";
import AlertsSettings from "@/pages/AlertsSettings";
import BridgeHealthBadge from "@/components/BridgeHealthBadge";
import Profile from "@/pages/Profile";
import Execution from "@/pages/Execution";
import OrderFlow from "@/pages/OrderFlow";
import OrderBook from "@/pages/OrderBook";
import RiskCompliance from "@/pages/RiskCompliance";
import Allocation from "@/pages/Allocation";
import MLLab from "@/pages/MLLab";
import BrokerLive from "@/pages/BrokerLive";
import Statistics from "@/pages/Statistics";
import PublicAudit from "@/pages/PublicAudit";
import Academy, { AcademyArticle } from "@/pages/Academy";
import RiskDisclosure from "@/pages/RiskDisclosure";
import { Terms, Privacy, Cookies as CookiesPage, MentionsLegales } from "@/pages/LegalPages";
import Analytics from "@/components/Analytics";
import CookieConsent from "@/components/CookieConsent";
import CotDashboard from "@/pages/CotDashboard";
import TemplateGallery from "@/pages/TemplateGallery";
import RiskAckModal from "@/components/RiskAckModal";
import BetaApplication from "@/pages/BetaApplication";
import FounderAdmin from "@/pages/FounderAdmin";
import Methodology from "@/pages/Methodology";
import ContactPage from "@/pages/ContactPage";
import TradeImport from "@/pages/TradeImport";
import PlatformsGuide from "@/pages/PlatformsGuide";

const NAV = [
  { to: "/app", key: "nav.dashboard", fallback: "Tableau de Bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true, simple: true },
  { to: "/app/execution", key: "nav.execution", fallback: "Exécution", icon: Zap, testid: "nav-execution", simple: true },
  { to: "/app/order-book", key: "nav.orderbook", fallback: "Order Book L2", icon: Book, testid: "nav-orderbook" },
  { to: "/app/broker-live", key: "nav.brokerlive", fallback: "Broker Live (OANDA)", icon: TrendingUp, testid: "nav-brokerlive" },
  { to: "/app/statistics", key: "nav.statistics", fallback: "Statistical Suite", icon: BarChart3, testid: "nav-statistics" },
  { to: "/app/import", key: "nav.import", fallback: "Importer mes trades", icon: UploadCloud, testid: "nav-import", simple: true },
  { to: "/app/guide", key: "nav.guide", fallback: "Guide d'utilisation", icon: HelpCircle, testid: "nav-guide", simple: true },
  { to: "/app/order-flow", key: "nav.orderflow", fallback: "Order Flow", icon: Layers, testid: "nav-orderflow" },
  { to: "/app/cot", key: "nav.cot", fallback: "COT Hebdo", icon: Landmark, testid: "nav-cot", simple: true },
  { to: "/app/gallery", key: "nav.gallery", fallback: "Galerie Templates", icon: Globe2, testid: "nav-gallery", simple: true },
  { to: "/app/ml-lab", key: "nav.mllab", fallback: "ML Lab", icon: Cpu, testid: "nav-mllab" },
  { to: "/app/risk", key: "nav.risk", fallback: "Risk & Compliance", icon: ShieldAlert, testid: "nav-risk" },
  { to: "/app/allocation", key: "nav.allocation", fallback: "Allocation", icon: PieChart, testid: "nav-allocation" },
  { to: "/app/watchlist", key: "nav.watchlist", fallback: "Watchlist", icon: ListChecks, testid: "nav-watchlist", simple: true },
  { to: "/app/backtest", key: "nav.backtest", fallback: "Backtest", icon: BarChart3, testid: "nav-backtest", simple: true },
  { to: "/app/sizer", key: "nav.sizer", fallback: "Taille Position", icon: Calculator, testid: "nav-sizer", simple: true },
  { to: "/app/prop-firm", key: "nav.propfirm", fallback: "Prop Firm", icon: Shield, testid: "nav-propfirm", simple: true },
  { to: "/app/my-accounts", key: "nav.myaccounts", fallback: "Mes Comptes Live", icon: Server, testid: "nav-my-accounts", simple: true },
  { to: "/app/leaderboard", key: "nav.leaderboard", fallback: "Leaderboard", icon: Trophy, testid: "nav-leaderboard", simple: true },
  { to: "/app/alerts", key: "nav.alerts", fallback: "Alertes Telegram", icon: Bell, testid: "nav-alerts", simple: true },
  { to: "/app/admin/affiliates", key: "nav.affiliates", fallback: "Affiliations", icon: DollarSign, testid: "nav-affiliates", simple: true, founderOnly: true },
  { to: "/app/profile", key: "nav.profile", fallback: "Profil & Journal", icon: User, testid: "nav-profile", simple: true },
  { to: "/app/lexique", key: "nav.lexique", fallback: "Lexique", icon: BookOpen, testid: "nav-lexique", simple: true },
  { to: "/app/signals", key: "nav.signals", fallback: "Historique", icon: Activity, testid: "nav-signals", simple: true },
  { to: "/app/downloads", key: "nav.downloads", fallback: "Téléchargements", icon: Download, testid: "nav-downloads" },
  { to: "/app/settings", key: "nav.settings", fallback: "Paramètres", icon: Settings2, testid: "nav-settings", simple: true },
  { to: "/app/founder-admin", key: "nav.founder", fallback: "Beta Applications", icon: Users, testid: "nav-founder", founderOnly: true },
];

function Shell({ children }) {
  const { t } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [uiMode, setUiMode] = useState(() => {
    try { return localStorage.getItem("qtr-ui-mode") || "advanced"; } catch { return "advanced"; }
  });
  const toggleMode = () => {
    const next = uiMode === "simple" ? "advanced" : "simple";
    setUiMode(next);
    try { localStorage.setItem("qtr-ui-mode", next); } catch { /* ignore */ }
  };
  const isFounder = user?.is_founder || user?.tier === "api";
  const [founderUnread, setFounderUnread] = useState(0);
  useEffect(() => {
    if (!isFounder) return;
    let alive = true;
    const load = () =>
      axios.get(`${API}/founder/unread-counts`)
        .then((r) => { if (alive) setFounderUnread(r.data.total || 0); })
        .catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [isFounder]);
  const filteredNav = (uiMode === "simple" ? NAV.filter((n) => n.simple) : NAV)
    .filter((n) => !n.founderOnly || isFounder);

  const handleLogout = () => {
    logout();
    navigate("/");
  };
  return (
    <div className={`min-h-screen flex bg-app text-white`}>
      <TrialEndedModal />
      <aside className="w-60 shrink-0 border-r border-white/10 bg-card px-3 py-5 hidden md:flex md:flex-col">
        <NavLink to="/" className="px-2 mb-6 block" data-testid="shell-home-link">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-8 h-8 rounded-md" width="32" height="32" />
            <div>
              <div className="font-heading font-bold tracking-tight text-[15px] leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 mt-1 uppercase tracking-widest">Outil d'Analyse Statistique</div>
            </div>
          </div>
        </NavLink>
        {user && (
          <div className="px-2 mb-4 text-xs" data-testid="shell-user-info">
            <div className="text-white/80 truncate">{user.email}</div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold ${
                user.tier === "institutional" ? "bg-accent-blue/20 text-accent-blue" :
                user.tier === "api" ? "bg-purple-500/20 text-purple-400" :
                user.tier === "signals" ? "bg-up/20 text-up" : "bg-white/10 text-white/60"
              }`}>{user.tier}</span>
              {user.is_beta_tester ? (
                <Link to="/app/beta-status" className="inline-flex items-center gap-1 rounded-full border border-cyan-500/50 bg-cyan-500/10 px-1.5 py-0.5 text-[9px] font-bold tracking-widest text-cyan-200 hover:bg-cyan-500/20" data-testid="beta-tester-badge">
                  <span className="h-1 w-1 rounded-full bg-cyan-400" /> BETA
                </Link>
              ) : (
                user.on_trial && user.trial_days_remaining != null && (
                  <span className="text-warn text-[10px]">Trial: {user.trial_days_remaining}j</span>
                )
              )}
            </div>
          </div>
        )}
        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto">
          {filteredNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors border ${
                  isActive ? "bg-white/5 border-white/10 text-white" : "text-white/60 border-transparent hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.8} />
              {t(n.key, n.fallback)}
              {n.testid === "nav-founder" && founderUnread > 0 && (
                <span
                  className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center"
                  data-testid="founder-unread-badge"
                >
                  {founderUnread > 99 ? "99+" : founderUnread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-2 px-2 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-xs" data-testid="shell-mode-toggle">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-white/50 uppercase tracking-widest text-[9px]">Mode</span>
            <span className={`text-[10px] font-semibold ${uiMode === "simple" ? "text-up" : "text-accent-blue"}`}>
              {uiMode === "simple" ? "SIMPLE" : "AVANCÉ"}
            </span>
          </div>
          <button
            onClick={toggleMode}
            className="w-full text-left text-[11px] text-white/60 hover:text-white transition"
            data-testid="shell-mode-toggle-btn"
          >
            {uiMode === "simple"
              ? `→ Passer en Avancé (${NAV.length - filteredNav.length} outils cachés)`
              : "→ Passer en Simple (masquer outils quant)"}
          </button>
        </div>
        <div className="space-y-2 mt-4 pt-3 border-t border-white/5">
          {user?.is_founder && (
            <NavLink to="/app/founder-admin" className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border ${isActive ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "bg-amber-500/10 border-amber-500/20 text-amber-300 hover:bg-amber-500/20"}`} data-testid="nav-founder-admin">
              <Crown size={16} /> Admin Fondateur
            </NavLink>
          )}
          <FeedbackButton />
          <LangSwitcher compact />
          <CurrencySwitcher compact />
          <BridgeHealthBadge />
          <DiscordCTA variant="compact" />
          <NavLink to="/pricing" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-accent-blue hover:bg-accent-blue/10" data-testid="nav-upgrade">
            <TrendingUp size={16} /> Upgrade
          </NavLink>
          {user && user.email !== "demo@algo-marche.local" && (
            <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5" data-testid="nav-logout">
              <LogOut size={16} /> Déconnexion
            </button>
          )}
        </div>
        <div className="text-[10px] text-white/40 px-2 mt-3 leading-relaxed">
          © 2026 — Vocabulaire propriétaire
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster theme="dark" richColors position="top-right" />
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <Analytics />
            <CookieConsent />
            <RiskAckModal />
            <Routes>
              {/* Public marketing pages */}
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/prop-firms" element={<PropFirmCompare />} />
              <Route path="/prop-firms/:slug" element={<PropFirmArticle />} />
              <Route path="/quiz" element={<PropFirmQuiz />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/audit" element={<PublicAudit />} />
              <Route path="/gallery" element={<TemplateGallery />} />
              <Route path="/academy" element={<Academy />} />
              <Route path="/academy/:slug" element={<AcademyArticle />} />
              <Route path="/risk-disclosure" element={<RiskDisclosure />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/beta" element={<BetaApplication />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/lexique" element={<LexiquePublic />} />
              <Route path="/plateformes" element={<PlatformsGuide />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/signup" element={<AuthPage mode="signup" />} />
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              {/* App (shell) */}
              <Route path="/app/*" element={
                <Shell>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/execution" element={<Execution />} />
                    <Route path="/order-book" element={<OrderBook />} />
                    <Route path="/broker-live" element={<BrokerLive />} />
                    <Route path="/statistics" element={<Statistics />} />
                    <Route path="/import" element={<TradeImport />} />
                    <Route path="/guide" element={<UserGuide />} />
                    <Route path="/order-flow" element={<OrderFlow />} />
                    <Route path="/cot" element={<CotDashboard />} />
                    <Route path="/gallery" element={<TemplateGallery />} />
                    <Route path="/ml-lab" element={<MLLab />} />
                    <Route path="/risk" element={<RiskCompliance />} />
                    <Route path="/allocation" element={<Allocation />} />
                    <Route path="/watchlist" element={<Watchlist />} />
                    <Route path="/backtest" element={<Backtest />} />
                    <Route path="/sizer" element={<PositionSizer />} />
                    <Route path="/prop-firm" element={<PropFirm />} />
                    <Route path="/my-accounts" element={<PropFirmAccounts />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route path="/alerts" element={<AlertsSettings />} />
                    <Route path="/admin/affiliates" element={<AdminAffiliates />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/lexique" element={<Lexique />} />
                    <Route path="/beta-status" element={<BetaStatus />} />
                    <Route path="/signals" element={<Signals />} />
                    <Route path="/downloads" element={<Downloads />} />
                    <Route path="/founder-admin" element={<FounderAdmin />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </Shell>
              } />
            </Routes>
          </BrowserRouter>
        </AppProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
